import { APP_MODULES_CONFIG } from "utils/features";
import { apiFetch } from "./apiFetch";

export const DEMO_MODE_ACTIVE = APP_MODULES_CONFIG.modeDemo;
export const OFFLINE_MODE_ACTIVE = DEMO_MODE_ACTIVE && process.env.REACT_APP_MODE_OFFLINE === "true";

// ─── Fallback files toggle (demo mode only) ───
const FALLBACK_FILES_KEY = "useFallbackFiles";

// ─── Authentication ───
/**
 * Headers for authenticated API requests (JSON + Bearer token).
 * @param {string|null} tokenOverride - Optional token; otherwise uses localStorage "token".
 */
export const getAuthHeaders = (tokenOverride = null) => {
  const token = tokenOverride ?? localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

/**
 * Reads the user's fallback files toggle from localStorage
 * Returns false when demo mode is off or the key is not set
 * @returns {boolean}
 */
export const getUseFallbackFiles = () => {
  if (!DEMO_MODE_ACTIVE) return false;
  return localStorage.getItem(FALLBACK_FILES_KEY) === "true";
};

/**
 * Persists the fallback-files toggle to localStorage
 * @param {boolean} value
 */
export const setUseFallbackFiles = (value) => {
  localStorage.setItem(FALLBACK_FILES_KEY, String(value));
};

/**
 * Removes the fallback files toggle from localStorage
 * Called when demo mode is off to ensure a stale state doesn't persist
 */
export const resetUseFallbackFiles = () => {
  localStorage.removeItem(FALLBACK_FILES_KEY);
};

// ─── API error helpers ───
/**
 * Extracts a string value from the possible "msg" | "message" properties of an API error response body
 *
 * @param {*} data - Parsed response body: object | string | null
 * @param {string} fallbackMessage - Fallback to return when no value could be resolved
 * @returns {string}
 */
export const getApiErrorMessage = (data, fallbackMessage) => {
  if (data == null) return fallbackMessage;
  if (typeof data === "string") return data.trim() || fallbackMessage;
  if (typeof data.message === "string" && data.message.trim()) return data.message;
  if (typeof data.msg === "string" && data.msg.trim()) return data.msg;
  return fallbackMessage;
};

/**
 * Extracts a string value from a "detail" field in any of the shapes FastAPI / Pydantic can produce:
 *   - plain string
 *   - array of validation errors
 *   - nested object with "msg" | "message" properties
 *
 * @param {*} detail - The raw value from the response body
 * @param {string} fallbackMessage - Fallback to return when no value could be resolved
 * @returns {string}
 */
export const getApiErrorDetail = (detail, fallbackMessage) => {
  if (detail == null) return fallbackMessage;
  if (typeof detail === "string") return detail.trim() || fallbackMessage;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first && typeof first === "object") {
      return first.msg || first.message || JSON.stringify(first);
    }
  }
  if (typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return fallbackMessage;
};

/**
 * Builds a standardised Error object from a failed fetch
 *
 * Using .text() and the JSON.parse() avoids using .clone() and duplicating the error content in memory
 * response.json() is the same as doing .text() followed by JSON.parse()
 *
 * @param {Response} response - The failed response data
 * @param {string} fallbackMessage - Fallback to return when no error details could be resolved
 * @returns {Promise<Error>}
 */
export const createApiError = async (response, fallbackMessage = "Request failed") => {
  const text = await response.text().catch(() => null);
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  const detail = getApiErrorDetail(payload?.detail, fallbackMessage);
  // Prefer detail over the generic fallback - If detail couldn't be found it's going to be the same string as the generic fallback
  const message = getApiErrorMessage(payload, detail);

  const error = new Error(message);
  error.status = response.status;
  error.detail = detail;
  error.rawDetail = payload?.detail;
  error.payload = payload;
  return error;
};

// ─── Main requests ───
/**
 * Performs a JSON-body request
 * Throws a standardised Error on fail
 * Not meant to be used directly by consumers
 */
const _jsonRequest = async (endpoint, method, { body, signal, errorMessage = "Request failed", tokenOverride = null } = {}) => {
  const response = await apiFetch(endpoint, {
    method,
    headers: getAuthHeaders(tokenOverride),
    ...(body !== undefined && { body: JSON.stringify(body) }),
    ...(signal && { signal }),
  });

  if (!response.ok) {
    throw await createApiError(response, errorMessage);
  }

  return await response.json();
};

/**
 * Performs a multipart/form-data request
 * Throws a standardised Error on fail
 * Not meant to be used directly by consumers
 */
const _multipartRequest = async (endpoint, method, { formData, signal, errorMessage = "Request failed" } = {}) => {
  const headers = getAuthHeaders();
  delete headers["Content-Type"];

  const response = await apiFetch(endpoint, {
    method,
    headers,
    body: formData,
    ...(signal && { signal }),
  });

  if (!response.ok) {
    throw await createApiError(response, errorMessage);
  }

  return await response.json();
};

// Explicit request wrappers - Meant to be used as middleware to avoid duplicating the fetch and response handling
export const performRequestTypeGET = (endpoint, options) => _jsonRequest(endpoint, "GET", options);

export const performRequestTypePOST = (endpoint, options) => _jsonRequest(endpoint, "POST", options);

export const performRequestTypePATCH = (endpoint, options) => _jsonRequest(endpoint, "PATCH", options);

export const performRequestTypePUT = (endpoint, options) => _jsonRequest(endpoint, "PUT", options);

export const performRequestTypeDELETE = (endpoint, options) => _jsonRequest(endpoint, "DELETE", options);

export const performRequestTypeMultipartPOST = (endpoint, options) => _multipartRequest(endpoint, "POST", options);

// ─── File URL handling ───
export const resolveFallbackUrl = (fallbackUrl) => {
  if (typeof fallbackUrl === "function") return fallbackUrl();
  if (typeof fallbackUrl === "string") return fallbackUrl;
  return `/file_.pdf?ts=${Date.now()}`;
};

/**
 * Resolve a language aware fallback URL for reference documents
 *
 * @returns {string} Local file fallback path
 */
export const getReferenceFallbackUrl = () => {
  const currentLang = localStorage.getItem("i18nextLng");
  const realLang = currentLang?.includes?.("en") ? "en" : "es";
  return realLang === "es"
    ? `/Formato_FCR-001_y_Manual_de_Certificacion.pdf?ts=${Date.now()}`
    : `/RCR-001_Form_and_Certification_Manual.pdf?ts=${Date.now()}`;
};

/**
 * Resolve a language aware fallback URL for evidence documents
 *
 * @returns {string} Local file fallback path
 */
export const getEvidenceFallbackUrl = () => {
  const currentLang = localStorage.getItem("i18nextLng");
  const realLang = currentLang?.includes?.("en") ? "en" : "es";
  return realLang === "es"
    ? `/Evidencia_de_Cumplimiento.pdf?ts=${Date.now()}`
    : `/Evidence of Compliance.pdf?ts=${Date.now()}`;
};

/**
 * Validates a presigned S3 url before exposing it
 *
 * Returns one of three states:
 *   true  - server confirmed reachable (response.ok === true)
 *   false - server confirmed NOT reachable (response.ok === false, non-2xx)
 *   null  - could not be determined (CORS block, network failure, timeout, external abort)
 *
 * @param {string} url - Presigned URL to validate
 * @param {AbortSignal} [externalSignal] - Optional, external abort signal controller
 * @returns {Promise<boolean|null>} true/false when confirmed, null when unknown
 */
export async function isPresignedUrlReachable(url, externalSignal) {
  const abortController = new AbortController();
  const timer = setTimeout(() => abortController.abort(), 4000);

  let externalListener;
  if (externalSignal) {
    externalListener = () => abortController.abort();
    externalSignal.addEventListener("abort", externalListener);
  }

  try {
    // Presigned URLs are signed for GET access
    const response = await fetch(url, { method: "GET", signal: abortController.signal });

    // Aborting immediately after headers arrive cancels the body download, only the headers are needed to validate the URL
    abortController.abort();

    if (!response.ok) return false;
    return true;
  } catch {
    // CORS block, network failure, timeout, or external abort — unkown status
    return null;
  } finally {
    clearTimeout(timer);
    if (externalSignal && externalListener) {
      externalSignal.removeEventListener("abort", externalListener);
    }
  }
}

/**
 * Fetch a presigned download URL from an API endpoint
 * Supports explicit path fallbacks for offline and demo modes
 * On error still returns a structured object for file handlers to define which path
 * to take, allowing file previews in the offline and demo mode special paths
 *
 * @param {string} endpoint - API URL to call
 * @param {Object} options
 * @param {string} [options.idField] - Property name for the id in the result since (e.g. "document_id", "evidence_id")
 * @param {*} [options.idValue] - Value to use for the id field when the API hasn't been called (offline/error fallback)
 * @param {AbortSignal} [options.signal] - Optional abort signal
 * @param {string} [options.errorMessage] - Error message when the API call fails
 * @param {string|Function} [options.fallbackUrl] - Custom fallback URL or function to return one for offline and demo unreachable file cases
 * @returns {Promise<{ download_url: string|null, url: string|null, mime_type: string|null, content_type: string|null, error: Error|null }>}
 */
export const fetchPresignedDownloadUrl = async (
  endpoint,
  {
    idField,
    idValue,
    signal,
    errorMessage = "Failed to download file",
    fallbackUrl,
  } = {}
) => {
  // Skip the API entirely when in offline mode or when the explicit demo mode fallback toggle is on
  // getUseFallbackFiles() returns false outside demo mode
  if (OFFLINE_MODE_ACTIVE || getUseFallbackFiles()) {
    const downloadUrl = resolveFallbackUrl(fallbackUrl);
    return {
      ...(idField && { [idField]: idValue }),
      download_url: downloadUrl,
      url: downloadUrl,
      mime_type: "application/pdf",
      content_type: "application/pdf",
      error: null,
    };
  }

  const response = await apiFetch(endpoint, {
    method: "GET",
    headers: getAuthHeaders(),
    ...(signal && { signal }),
  }, { skipAuthHandling: true });

  if (!response.ok) {
    const downloadUrl = DEMO_MODE_ACTIVE ? resolveFallbackUrl(fallbackUrl) : null;
    return {
      ...(idField && { [idField]: idValue }),
      download_url: downloadUrl,
      url: downloadUrl,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      content_type: DEMO_MODE_ACTIVE ? "application/pdf" : null,
      error: DEMO_MODE_ACTIVE ? null : await createApiError(response, errorMessage),
    };
  }

  const content = await response.json();

  // Handle both object responses ({ download_url: "..." }) and raw string responses ("...")
  const rawUrl = typeof content === "string"
    ? content
    : (content?.download_url || content?.url || null);
  const cleanUrl = rawUrl ? rawUrl.replace(/(^"|"$)/g, "") : null;
  const reachability = cleanUrl ? await isPresignedUrlReachable(cleanUrl, signal) : false;

  // Confirmed bad (false) or unknown (null) while in demo mode - Try to use fall back
  // Reachability being null is paired with demo mode flag before entering the statement because FilePreviewer has it's own check
  // to try and render urls that we couldn't verify as reachable so it needs to skip this return for that to actually happen
  if (reachability === false || (reachability === null && DEMO_MODE_ACTIVE)) {
    const downloadUrl = DEMO_MODE_ACTIVE ? resolveFallbackUrl(fallbackUrl) : null;
    return {
      ...(idField && { [idField]: typeof content === "object" ? (content?.[idField] ?? idValue) : idValue }),
      download_url: downloadUrl,
      url: downloadUrl,
      mime_type: DEMO_MODE_ACTIVE ? "application/pdf" : (content?.mime_type || content?.content_type || null),
      content_type: DEMO_MODE_ACTIVE ? "application/pdf" : (content?.content_type || content?.mime_type || null),
      error: DEMO_MODE_ACTIVE ? null : new Error("File URL is unreachable or invalid"),
    };
  }

  // Confirmed good (true) or unknown (null) not in demo mode
  return {
    ...(idField && { [idField]: typeof content === "object" ? (content?.[idField] ?? idValue) : idValue }),
    download_url: cleanUrl,
    url: cleanUrl,
    mime_type: typeof content === "object" ? (content?.mime_type || content?.content_type || null) : null,
    content_type: typeof content === "object" ? (content?.content_type || content?.mime_type || null) : null,
    error: null,
  };
};

// ─── Bulk progress handling - Both real and simulated ───
/**
 * Read a fetch response body with progress based on Content-Length.
 * @param {ReadableStreamDefaultReader} reader
 * @param {number} contentLength
 * @param {Function} onProgress - (percent: number) => void
 * @returns {Promise<string>} Decoded response text.
 */
export const getRequestProgress = async (
  reader,
  contentLength,
  onProgress = () => { }
) => {
  try {
    let receivedLength = 0;
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      chunks.push(value);
      receivedLength += value.length;

      const percent = contentLength
        ? Math.min((receivedLength / contentLength) * 100, 100)
        : 0;
      onProgress(percent);
    }

    const combined = new Uint8Array(receivedLength);
    let position = 0;
    for (const chunk of chunks) {
      combined.set(chunk, position);
      position += chunk.length;
    }

    return new TextDecoder("utf-8").decode(combined);
  } catch (error) {
    throw error;
  }
};

/**
 * Estimate a simulated duration (ms) for a bulk operation based on item count
 * Clamped to the 2–10 second range to keep the progress bar feeling
 * responsive without jumping to completion instantly
 *
 * @param {number} itemCount - Number of items being processed.
 * @returns {number} Estimated duration in milliseconds.
 */
const estimateBulkDuration = (itemCount) => Math.min(Math.max((itemCount || 1) * 100, 2000), 10000);

/**
 * Start a simulated progress timer that eases towards 95%
 *
 * @param {number} estimatedDuration - Total estimated duration in ms
 * @param {Function} onProgress - Callback receiving a 0–95 integer
 * @returns {{ clear: () => void }} Object with a clear() method to stop the timer
 */
const startSimulatedProgress = (estimatedDuration, onProgress) => {
  const startTime = Date.now();
  let progressInterval;

  const updateProgress = () => {
    const elapsed = Date.now() - startTime;
    const ratio = Math.min(elapsed / estimatedDuration, 1);
    const easedProgress = 1 - Math.pow(1 - ratio, 3);
    const progress = Math.floor(easedProgress * 95);
    onProgress(progress);
    if (ratio >= 1) clearInterval(progressInterval);
  };

  progressInterval = setInterval(updateProgress, 100);

  return { clear: () => clearInterval(progressInterval) };
};

/**
 * Shared bulk-operation simulator
 *
 * Runs a simulated progress bar while the real request is in running in the background and jumps to 100% on completion
 * This is a temporary solution until websocket/SSE is implemented for these operations
 *
 * @param {Object} params
 * @param {string} params.endpoint - API URL path
 * @param {Object} params.fetchOptions - Fetch options to send to apiFetch (method, headers, body, signal, …).
 * @param {number} [params.itemCount=1] - Number of items being processed
 * @param {number} [params.estimatedDuration] - Explicit duration in ms
 * @param {Function} [params.onProgress] - Progress callback receiving a 0–100 integer
 * @param {string} [params.errorLabel] - Label used as an error log on failure
 * @returns {Promise<any>} Parsed JSON response body
 */
export const runBulkWithSimulatedProgress = async ({
  endpoint,
  fetchOptions,
  itemCount = 1,
  estimatedDuration,
  onProgress = () => { },
  errorLabel = "Bulk operation error",
}) => {
  const duration = estimatedDuration ?? estimateBulkDuration(itemCount);

  try {
    const progress = startSimulatedProgress(duration, onProgress);

    const response = await apiFetch(endpoint, fetchOptions);

    progress.clear();
    onProgress(100);

    if (!response.ok) {
      throw await createApiError(response, errorLabel ?? "Request failed");
    }

    return await response.json();
  } catch (error) {
    console.error(`${errorLabel}:`, error);
    throw error;
  }
};